/**
 * Conversation content zoom — the pure width→factor function behind
 * `ConversationShell`'s compact mode.
 *
 * ## Why this exists
 *
 * The chat conversation is normally a wide-screen surface, but it gets squeezed
 * into a left column whenever a focus-pane viewer opens on the right (consulting
 * a channel thread / contacts while still prompting the concierge). At full
 * reading size the prose then wraps into a cramped wall of short lines.
 *
 * The fix is a CSS `zoom` on the whole conversation column — chosen over a
 * font-only type scale precisely because the lawyer wants a faithful MINIATURE
 * of the wide layout (text, spacing, avatars, the composer all shrink together),
 * not tiny text swimming in full-size chrome. `zoom` also reflows (unlike
 * `transform: scale`), so the shrunk text re-wraps to fill the narrow column.
 *
 * ## Why the factor is WIDTH-driven, not "is a pane open"
 *
 * The lawyer is on a desktop browser and can widen the window at any time. So
 * the real signal is the column's ACTUAL measured width, not the cause of it:
 *
 *   - column at/above the comfortable reading measure ⇒ factor 1 (NO effect) —
 *     a full-width window is never zoomed, pane or not;
 *   - below it ⇒ scale down proportionally, floored so it never goes unreadable;
 *   - maximise the window ⇒ the column crosses back above the measure ⇒ the
 *     factor climbs back to 1 on its own (the ResizeObserver re-fires).
 *
 * Quantised to `ZOOM_STEP` so a window drag doesn't thrash a reflow on every
 * pixel — the factor only changes in visible steps.
 */

/** The column width (px) at/above which the conversation reads comfortably and
 *  is NOT zoomed. Matches the prose reading column's own max width
 *  (`mx-auto max-w-[720px]`) plus its horizontal breathing room, so zoom
 *  engages right as the column starts biting into the text measure. */
export const COMFORTABLE_WIDTH_PX = 760;

/**
 * The smallest factor we will apply — below this, legal reading text gets too
 * small to be worth it, so the column just stays at the floor and relies on the
 * narrow layout.
 *
 * ⚖ **RAISED 0.65 → 0.80 (operator, 2026-08-10):** *"the compacted conversation
 * pane is too small, fonts should be a bit bigger."*
 *
 * The floor is not a tuning knob here — it IS the viewer-open experience.
 * Measured at the width the ruling was made about (pane 447px): 447/760 = 0.588
 * clamps to the floor, so EVERY viewer-open conversation renders at exactly
 * this number and nothing else. At 0.65 a 16px body became 10.4px; at 0.80 it
 * is 12.8px.
 *
 * ⚠ The old comment called 10.4px "the readable-floor for a glance-and-return
 * mode". That was a judgement about a mode the lawyer does not actually use
 * this pane for — they read the transcript here while consulting a document,
 * which is reading, not glancing. Cost: the column lays out at `width / 0.80`
 * = 559px instead of 688px, so lines wrap shorter. That is the trade the
 * ruling makes, deliberately.
 */
export const MIN_ZOOM = 0.8;

/** Factor granularity. The zoom only moves in 5% steps, so a continuous drag of
 *  the pane divider re-lays-out the transcript a handful of times, not on every
 *  animation frame. */
export const ZOOM_STEP = 0.05;

/** Round to the nearest `ZOOM_STEP`. */
function quantize(factor: number): number {
  return Math.round(factor / ZOOM_STEP) * ZOOM_STEP;
}

/**
 * The `zoom` factor for a conversation column of `width` px.
 *
 * `1` at/above the comfortable measure (no effect), scaling linearly down to
 * `MIN_ZOOM` as the column narrows, quantised to `ZOOM_STEP`. A zero/unknown
 * width (pre-measurement) reads as `1` so the first paint is never a flash of
 * shrunk text that then grows.
 */
export function zoomForWidth(width: number): number {
  if (!Number.isFinite(width) || width <= 0) return 1;
  if (width >= COMFORTABLE_WIDTH_PX) return 1;
  const raw = width / COMFORTABLE_WIDTH_PX;
  const clamped = Math.max(MIN_ZOOM, Math.min(1, raw));
  // Quantise, then re-clamp: rounding a value just above the floor could dip
  // below it (e.g. 0.66 → 0.65 is fine, but guard the general case).
  return Math.max(MIN_ZOOM, Math.min(1, quantize(clamped)));
}
